import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../stores/auth-store';
import { env } from './env';

export interface RefreshResponse {
  accessToken: string;
  expiresIn: number;
}

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(client: AxiosInstance): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = client
      .post<RefreshResponse>('/v1/auth/refresh')
      .then((res) => {
        const { accessToken } = res.data;
        const store = useAuthStore.getState();
        if (store.user) {
          store.setSession(accessToken, store.user);
        } else {
          useAuthStore.setState({ accessToken });
        }
        return accessToken;
      })
      .catch(() => {
        useAuthStore.getState().clear();
        return null;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: env.apiBaseUrl,
  withCredentials: true,
  timeout: 30_000,
});

apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token && !config.headers.has('Authorization')) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

apiClient.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined;
    const status = error.response?.status;
    const url = original?.url ?? '';

    const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/refresh') || url.includes('/auth/signup');

    if (status === 401 && original && !original._retry && !isAuthEndpoint) {
      original._retry = true;
      const fresh = await refreshAccessToken(apiClient);
      if (fresh) {
        original.headers.set('Authorization', `Bearer ${fresh}`);
        return apiClient(original);
      }
    }
    return Promise.reject(error);
  },
);
